import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlImportViews } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { safeJsonParse } from '../../util/DataUtils';
import { apiFetch } from '../../util/ProjectUtils';

export interface IImportView {
    name: string;
    preload?: boolean;
}

export type IImportViewsState = SlicePropsWithData<IImportView[]>;

const thunks = {
    getImportViews: async (config: { replicaId: string }): Promise<IImportView[]> => {
        try {
            const { replicaId } = config;

            const url = urlImportViews.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            return safeJsonParse<IImportView[]>(await response.text(), []);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IImportViewsState>) => {
        builder.addCase(getImportViews.pending, (state: Draft<IImportViewsState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getImportViews.fulfilled,
            (state: Draft<IImportViewsState>, action: IAction<IImportView[]>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getImportViews.rejected,
            (state: Draft<IImportViewsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = [];
            },
        );
    },
};

export const getImportViews = createAsyncThunk('import/views', thunks.getImportViews);

export const importViewsSlice = createSlice({
    name: 'importViews',
    extraReducers: thunks._reducers,
    initialState: {
        data: [],
        loading: false,
        error: null,
    },
    reducers: {},
});

export default importViewsSlice.reducer;
